import React from 'react';
import { motion } from 'framer-motion';
import { FormFileUpload } from '../forms';

interface MediaManagementProps {
  logoUrl: string;
  logo: File | null;
  logoUploading: boolean;
  galleryImages: string[];
  newGalleryImages: File[];
  galleryUploading: boolean;
  shopVideoUrl?: string;
  shopVideo?: File | null;
  videoUploading?: boolean;
  videoDeleting?: boolean;
  onLogoChange: (file: File | null) => void;
  onLogoUpload: () => void;
  onGalleryImagesChange: (files: File[]) => void;
  onGalleryUpload: () => void;
  onDeleteGalleryImage: (imageUrl: string) => void;
  onReorderGallery: (fromIndex: number, toIndex: number) => void;
  onVideoChange?: (file: File | null) => void;
  onVideoUpload?: () => void;
  onVideoDelete?: () => void;
}

export const MediaManagement: React.FC<MediaManagementProps> = ({
  logoUrl,
  logo,
  logoUploading,
  galleryImages,
  newGalleryImages,
  galleryUploading,
  shopVideoUrl = "",
  shopVideo = null,
  videoUploading = false,
  videoDeleting = false,
  onLogoChange,
  onLogoUpload,
  onGalleryImagesChange,
  onGalleryUpload,
  onDeleteGalleryImage,
  onReorderGallery,
  onVideoChange,
  onVideoUpload,
  onVideoDelete
}) => {
  // Ensure safe prop handling to prevent React Error #130
  const safeLogoUrl = (typeof logoUrl === 'string' && logoUrl.trim()) ? logoUrl.trim() : '';
  const safeGalleryImages = Array.isArray(galleryImages) ?
    galleryImages.filter(img => typeof img === 'string' && img.trim().length > 0) : [];
  const safeVideoUrl = (typeof shopVideoUrl === 'string' && shopVideoUrl.trim()) ? shopVideoUrl.trim() : '';
  const videoInputRef = React.useRef<HTMLInputElement | null>(null);
  
  // Debug logging - moved outside JSX to prevent rendering issues
  React.useEffect(() => {
    console.log('MediaManagement props received:', {
      logoUrl: safeLogoUrl,
      logoUrlLength: safeLogoUrl.length,
      galleryImages: safeGalleryImages,
      galleryImagesLength: safeGalleryImages.length,
      shopVideoUrl: safeVideoUrl,
      videoUrlLength: safeVideoUrl.length
    });
  }, [safeLogoUrl, safeGalleryImages, safeVideoUrl]);

  return (
    <div className="space-y-8">
      {/* Logo Upload Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Business Logo</h3>
        
        {/* Side-by-side layout when logo exists */}
        <div className={`${safeLogoUrl ? 'grid grid-cols-2 gap-6' : 'space-y-4'}`}>
          {/* Upload Section */}
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Logo Requirements:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Size: 600 x 600 pixels (square)</li>
                <li>• Format: JPG or PNG only</li>
                <li>• File size: Under 1MB</li>
                <li>• High quality, clear image</li>
              </ul>
            </div>
            
            <FormFileUpload
              label={safeLogoUrl ? "Change Logo" : "Upload Business Logo"}
              name="business-logo"
              accept="image/jpeg,image/jpg,image/png"
              onChange={(file) => onLogoChange(file)}
              value={logo}
              helpText="Select a 600x600px JPG or PNG file under 1MB"
            />
            
            {logo && (
              <button
                onClick={onLogoUpload}
                disabled={logoUploading}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {logoUploading ? "Uploading..." : "Upload Logo"}
              </button>
            )}
          </div>
          
          {/* Logo Display Section - Only show when logo exists */}
          {safeLogoUrl && (
            <div className="flex flex-col items-center justify-center">
              <div className="text-center mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Current Logo</h4>
                <img
                  src={safeLogoUrl}
                  alt="Business Logo"
                  className="h-40 w-40 object-contain rounded-lg border-2 border-gray-200 shadow-sm"
                  onError={(e) => {
                    console.warn('Failed to load logo:', safeLogoUrl);
                    const img = e.target as HTMLImageElement;
                    if (img && img.parentElement) {
                      img.parentElement.style.display = 'none';
                    }
                  }}
                  onLoad={() => {
                    console.log('Logo loaded successfully:', safeLogoUrl);
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 text-center">
                This logo will appear on your business profile
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Shop Video Upload Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Shop Video</h3>
        
        {/* Side-by-side layout when video exists */}
        <div className={`${safeVideoUrl ? 'grid grid-cols-2 gap-6' : 'space-y-4'}`}>
          {/* Upload Section */}
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-purple-800 mb-2">Video Requirements:</h4>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Format: MP4, WebM, OGG, or MOV</li>
                <li>• File size: Under 500MB</li>
                <li>• Quality: HD recommended (1080p)</li>
                <li>• Content: Shop tour, introduction, or services showcase</li>
              </ul>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {safeVideoUrl ? "Change Shop Video" : "Upload Shop Video"}
              </label>
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/quicktime"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (onVideoChange) {
                    onVideoChange(file);
                  }
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload a video showcasing your shop (max 500MB, MP4/WebM/OGG/MOV)
              </p>
              {shopVideo && (
                <p className="mt-2 text-sm text-purple-700">
                  Selected file: {shopVideo.name}
                </p>
              )}
            </div>
            
            {shopVideo && onVideoUpload && (
              <button
                onClick={onVideoUpload}
                disabled={videoUploading}
                className="w-full bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {videoUploading ? "Uploading..." : "Upload Video"}
              </button>
            )}
          </div>
          
          {/* Video Display Section - Only show when video exists */}
          {safeVideoUrl && (
            <div className="flex flex-col items-center justify-center">
              <div className="text-center mb-4 w-full">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Current Shop Video</h4>
                <video
                  src={safeVideoUrl}
                  controls
                  className="w-full max-h-64 object-contain rounded-lg border-2 border-gray-200 shadow-sm"
                  onError={(e) => {
                    console.warn('Failed to load video:', safeVideoUrl);
                    const video = e.target as HTMLVideoElement;
                    if (video && video.parentElement) {
                      video.parentElement.innerHTML = '<p class="text-red-500 text-sm">Failed to load video</p>';
                    }
                  }}
                  onLoadedData={() => {
                    console.log('Video loaded successfully:', safeVideoUrl);
                  }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mb-3">
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={videoUploading || videoDeleting}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Replace Video
                </button>
                <button
                  type="button"
                  onClick={onVideoDelete}
                  disabled={!onVideoDelete || videoUploading || videoDeleting}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {videoDeleting ? "Deleting..." : "Delete Video"}
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                This video will appear on your shop page
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Gallery Management Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Business Gallery</h3>
        
        <div className="space-y-6">
          {/* Current Gallery Images */}
          {safeGalleryImages.length > 0 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Current Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {safeGalleryImages.map((imageUrl, index) => (
                  <div key={`gallery-${index}-${imageUrl.substring(0, 20)}`} className="relative group">
                    <img
                      src={imageUrl}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                      onError={(e) => {
                        console.warn('Failed to load gallery image:', imageUrl);
                        const img = e.target as HTMLImageElement;
                        if (img && img.parentElement) {
                          img.parentElement.style.display = 'none';
                        }
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
                      <button
                        onClick={() => onDeleteGalleryImage(imageUrl)}
                        className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
                        title="Delete Image"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      {index > 0 && (
                        <button
                          onClick={() => onReorderGallery(index, index - 1)}
                          className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
                          title="Move Left"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                      )}
                      {index < safeGalleryImages.length - 1 && (
                        <button
                          onClick={() => onReorderGallery(index, index + 1)}
                          className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
                          title="Move Right"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload New Images */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Add New Images</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Gallery Images
              </label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  onGalleryImagesChange(files);
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="mt-1 text-sm text-gray-500">
                Upload business photos (max 5MB each, JPG/PNG/WebP). Current: {safeGalleryImages.length}/10 images
              </p>
            </div>
            
            {Array.isArray(newGalleryImages) && newGalleryImages.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">
                  Selected {newGalleryImages.length} new image(s):
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {newGalleryImages.map((file, index) => (
                    <ImagePreview
                      key={`preview-${index}-${file.name}`}
                      file={file}
                      index={index}
                      onRemove={() => {
                        const updatedFiles = newGalleryImages.filter((_, i) => i !== index);
                        onGalleryImagesChange(updatedFiles);
                      }}
                    />
                  ))}
                </div>
                <button
                  onClick={onGalleryUpload}
                  disabled={galleryUploading}
                  className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {galleryUploading ? "Uploading..." : `Upload ${Array.isArray(newGalleryImages) ? newGalleryImages.length : 0} Image(s)`}
                </button>
              </div>
            )}
          </div>

          {(!Array.isArray(galleryImages) || galleryImages.length === 0) && (!Array.isArray(newGalleryImages) || newGalleryImages.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                No gallery images yet. Upload some photos to showcase your business!
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Separate component for image preview to avoid CSP issues with blob URLs
const ImagePreview: React.FC<{
  file: File;
  index: number;
  onRemove: () => void;
}> = ({ file, index, onRemove }) => {
  const [imageSrc, setImageSrc] = React.useState<string>('');
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === 'string') {
        setImageSrc(e.target.result);
      }
    };
    
    reader.onerror = () => {
      console.warn('Failed to read preview image');
      setLoadError(true);
    };
    
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.warn('Failed to create preview for file:', error);
      setLoadError(true);
    }

    return () => {
      // Cleanup if needed
      setImageSrc('');
      setLoadError(false);
    };
  }, [file]);

  if (loadError) {
    return (
      <div className="relative w-full h-32 bg-gray-200 rounded-lg border-2 border-gray-200 flex items-center justify-center">
        <span className="text-gray-500 text-sm">Preview unavailable</span>
        <div className="absolute top-2 right-2">
          <button
            onClick={onRemove}
            className="bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {imageSrc ? (
        <img
          src={imageSrc}
          alt={`Preview ${index + 1}`}
          className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
          onError={() => {
            console.warn('Failed to load preview image');
            setLoadError(true);
          }}
        />
      ) : (
        <div className="w-full h-32 bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center">
          <span className="text-gray-500 text-sm">Loading...</span>
        </div>
      )}
      <div className="absolute top-2 right-2">
        <button
          onClick={onRemove}
          className="bg-red-600 text-white p-1 rounded-full hover:bg-red-700 transition-colors"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};